import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { Link } from "react-router";
import styled from "styled-components";
import instantLiveBlueImage from "@assets/images/live/instant_live_blue.svg";
import scheduledLiveYellowImage from "@assets/images/live/scheduled_live_yellow.svg";
import { Path } from "@core/routing/path";
import { Group } from "@domain/group/group";
import { Workspace } from "@domain/group/workspace/workspace";
import { LivecourseType } from "@domain/livecourse/livecourse";
import { Primary } from "@ui/components/containers/primary";
import { ClosableModal } from "@ui/modals/closableModal";
import { colors } from "@ui/styles/colors";
import { gap, mobile } from "@ui/styles/utils";

interface GroupLivecoursesChooserModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	shareroom: Group;
	initialWorkspace?: Workspace;
}

interface GroupLivecoursesMobileModalProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroom: Group;
	initialWorkspace?: Workspace;
}

interface OrganizationLivecoursesChooserModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	organizationId: string;
}

interface OrganizationLivecoursesMobileModalProps extends React.HTMLAttributes<HTMLDivElement> {
	organizationId: string;
}
export const GroupLivecoursesChooserModal: React.FC<GroupLivecoursesChooserModalProps> = ({
	initialWorkspace,
	shareroom,
	close,
	...htmlProps
}) => {
	const [instant, seVisioInstant] = useState(false);
	const [scheduled, seVisioScheduled] = useState(false);
	return (
		<Container {...htmlProps} close={close}>
			<VisioInstant
				active={instant}
				onMouseEnter={() => seVisioInstant(true)}
				onMouseLeave={() => seVisioInstant(false)}
			>
				<Link to={Path.LiveGroupEditor(shareroom.id, LivecourseType.INSTANT, "new")}>
					<img src={instantLiveBlueImage} width={90} height={90} />
					<TextDescription>{I18n.t("live.instant")}</TextDescription>
				</Link>
			</VisioInstant>

			<VisioScheduled
				active={scheduled}
				onMouseEnter={() => seVisioScheduled(true)}
				onMouseLeave={() => seVisioScheduled(false)}
			>
				<Link to={Path.LiveGroupEditor(shareroom.id, LivecourseType.SCHEDULED, "new")}>
					<img src={scheduledLiveYellowImage} width={90} height={90} />
					<TextDescription>{I18n.t("live.scheduled")}</TextDescription>
				</Link>
			</VisioScheduled>
		</Container>
	);
};

export const GroupLivecoursesMobileModal: React.FC<GroupLivecoursesMobileModalProps> = ({ shareroom }) => {
	const [instant, seVisioInstant] = useState(false);
	const [scheduled, seVisioScheduled] = useState(false);
	return (
		<Content>
			<MenuSwitch onClick={() => undefined}>
				<VisioInstant
					active={instant}
					onMouseEnter={() => seVisioInstant(true)}
					onMouseLeave={() => seVisioInstant(false)}
				>
					<Link to={Path.LiveGroupEditor(shareroom.id, LivecourseType.INSTANT, "new")}>
						<img src={instantLiveBlueImage} width={90} height={90} />
						<TextDescription>{I18n.t("live.instant")}</TextDescription>
					</Link>
				</VisioInstant>
			</MenuSwitch>
			<MenuSwitch onClick={() => undefined}>
				<VisioScheduled
					active={scheduled}
					onMouseEnter={() => seVisioScheduled(true)}
					onMouseLeave={() => seVisioScheduled(false)}
				>
					<Link to={Path.LiveGroupEditor(shareroom.id, LivecourseType.SCHEDULED, "new")}>
						<img src={scheduledLiveYellowImage} width={90} height={90} />
						<TextDescription>{I18n.t("live.scheduled")}</TextDescription>
					</Link>
				</VisioScheduled>
			</MenuSwitch>
		</Content>
	);
};

export const OrganizationLivecoursesChooserModal: React.FC<OrganizationLivecoursesChooserModalProps> = ({
	organizationId,
	close,
	...htmlProps
}) => {
	const [instant, seVisioInstant] = useState(false);
	const [scheduled, seVisioScheduled] = useState(false);
	return (
		<Container {...htmlProps} close={close}>
			<VisioInstant
				active={instant}
				onMouseEnter={() => seVisioInstant(true)}
				onMouseLeave={() => seVisioInstant(false)}
			>
				<Link to={Path.LiveOrganizationEditor(organizationId, LivecourseType.INSTANT, "new")}>
					<img src={instantLiveBlueImage} width={90} height={90} />
					<TextDescription>{I18n.t("live.instant")}</TextDescription>
				</Link>
			</VisioInstant>

			<VisioScheduled
				active={scheduled}
				onMouseEnter={() => seVisioScheduled(true)}
				onMouseLeave={() => seVisioScheduled(false)}
			>
				<Link to={Path.LiveOrganizationEditor(organizationId, LivecourseType.SCHEDULED, "new")}>
					<img src={scheduledLiveYellowImage} width={90} height={90} />
					<TextDescription>{I18n.t("live.scheduled")}</TextDescription>
				</Link>
			</VisioScheduled>
		</Container>
	);
};

export const OrganizationLivecoursesMobileModal: React.FC<OrganizationLivecoursesMobileModalProps> = ({
	organizationId,
}) => {
	const [instant, seVisioInstant] = useState(false);
	const [scheduled, seVisioScheduled] = useState(false);
	return (
		<Content>
			<MenuSwitch onClick={() => undefined}>
				<VisioInstant
					active={instant}
					onMouseEnter={() => seVisioInstant(true)}
					onMouseLeave={() => seVisioInstant(false)}
				>
					<Link to={Path.LiveOrganizationEditor(organizationId, LivecourseType.INSTANT, "new")}>
						<img src={instantLiveBlueImage} width={90} height={90} />
						<TextDescription>{I18n.t("live.instant")}</TextDescription>
					</Link>
				</VisioInstant>
			</MenuSwitch>
			<MenuSwitch onClick={() => undefined}>
				<VisioScheduled
					active={scheduled}
					onMouseEnter={() => seVisioScheduled(true)}
					onMouseLeave={() => seVisioScheduled(false)}
				>
					<Link to={Path.LiveOrganizationEditor(organizationId, LivecourseType.SCHEDULED, "new")}>
						<img src={scheduledLiveYellowImage} width={90} height={90} />
						<TextDescription>{I18n.t("live.scheduled")}</TextDescription>
					</Link>
				</VisioScheduled>
			</MenuSwitch>
		</Content>
	);
};

const Content = styled.div`
	${gap(10)};
	font-size: 0.8125rem;
`;

const MenuSwitch = styled(Primary).withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>`
	cursor: pointer;
	${({ selected, theme }) => selected && `color: ${theme.colors.secondary}`}
`;

const VisioInstant = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>`
	display: flex;
	flex: 1;
	flex-direction: column;
	align-items: center;
	${gap(20)};
	padding-top: 25px;
	margin-right: 10px;
	height: 200px;
	//width: 247px;
	font-weight: bold;
	border-radius: 25px;
	border: solid 1px ${({ active }) => (active ? colors.secondary : colors.white)};
	color: ${({ active }) => (active ? colors.secondary : "#737373")};
	background-color: ${({ active }) => (active ? colors.secondaryLight : colors.white)};

	@media ${mobile} {
		//width: 0
		text-align: center;
	}
`;

const VisioScheduled = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>`
	display: flex;
	flex: 1;
	flex-direction: column;
	align-items: center;
	${gap(20)};
	padding-top: 25px;
	margin-right: 10px;
	height: 200px;
	//width: 247px;
	border-radius: 15px;
	font-weight: bold;
	border: solid 1px ${({ active }) => (active ? colors.secondary : colors.white)};
	color: ${({ active }) => (active ? colors.secondary : "#737373")};
	background-color: ${({ active }) => (active ? colors.secondaryLight : colors.white)};
	@media ${mobile} {
		//width: 0
		text-align: center;
	}
`;

const TextDescription = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	font-size: 14px;
	margin-top: 10px;
`;

const Container = styled(ClosableModal)`
	flex-direction: row;
	align-items: center;
	text-align: center;
	height: 280px;
	width: 574px;
`;
